import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import API from '../config';

const perks = [
  {
    icon: <CheckIcon />,
    title: 'No More Wasted Money',
    description: 'Make informed decisions and avoid creators who don\'t deliver',
  },
  {
    icon: <PeopleIcon />,
    title: 'Access to 1000+ Creators',
    description: 'Complete database of verified creators with real subscriber reviews',
  },
  {
    icon: <StarIcon />,
    title: 'Exclusive Reviews & Ratings',
    description: 'Authentic reviews from real subscribers who spent money',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'VIP Leaderboard Access',
    description: 'See top-rated creators worth your subscription dollars',
  },
];

export default function UpgradeModal({ open, onClose }) {
  const { authData } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const theme = useTheme();

  // Fetch plans from API
  useEffect(() => {
    if (open && authData?.access_token) {
      fetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, authData]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/api/subscription/plans`, {
        headers: {
          'Authorization': `Bearer ${authData.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const { plans: fetchedPlans } = await response.json();
        setPlans(fetchedPlans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Check if yearly is available
  const yearlyPlan = plans.find(p => p.interval === 'year');
  const yearlyAvailable = !!yearlyPlan;
  
  // Filter plans by billing interval
  const filteredPlans = plans.filter(plan => plan.interval === billingInterval);
  const selectedPlanData = filteredPlans[0];

  // Calculate savings
  const calculateSavings = () => {
    const monthlyPlan = plans.find(p => p.interval === 'month');
    const yearlyPlan = plans.find(p => p.interval === 'year');
    
    if (monthlyPlan && yearlyPlan) {
      const monthlyYearlyPrice = monthlyPlan.unitAmount * 12;
      const yearlyPrice = yearlyPlan.unitAmount;
      const savings = ((monthlyYearlyPrice - yearlyPrice) / monthlyYearlyPrice) * 100;
      return Math.round(savings);
    }
    return 0;
  };

  const savingsPercent = calculateSavings();
  
  // If yearly is not available, default to monthly
  useEffect(() => {
    if (!yearlyAvailable && billingInterval === 'year') {
      setBillingInterval('month');
    }
  }, [yearlyAvailable, billingInterval]);

  const handleSubscribe = async () => {
    if (!authData?.access_token || !selectedPlanData) {
      console.error('No auth token or selected plan available');
      return;
    }

    setLoading(true);
    try {
      // Create subscription with selected plan
      const subscriptionResponse = await fetch(`${API}/api/subscription/create-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authData.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: selectedPlanData.priceId,
        }),
      });

      if (!subscriptionResponse.ok) {
        throw new Error('Failed to create subscription');
      }

      const { checkoutUrl } = await subscriptionResponse.json();
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemPromo = async () => {
    const normalizedCode = promoCode.trim().toUpperCase();
    if (!normalizedCode || !authData?.access_token) {
      setPromoError('Enter your promo code.');
      return;
    }

    setPromoLoading(true);
    setPromoError('');
    try {
      const response = await fetch(`${API}/api/subscription/redeem-promo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authData.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: normalizedCode }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Could not apply that promo code.');
      }

      onClose();
      window.location.reload();
    } catch (error) {
      setPromoError(error.message || 'Could not apply that promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
          backdropFilter: 'blur(20px)',
        }
      }}
    >
      <DialogTitle sx={{ 
        position: 'relative',
        textAlign: 'center',
        pb: 1,
        pt: 3
      }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<StarIcon />}
            label="OnlyStats VIP"
            color="primary"
            variant="filled"
            sx={{
              fontSize: '0.9rem',
              fontWeight: 600,
              px: 2,
              py: 1,
              height: 'auto',
            }}
          />
        </Box>
        
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Unlock Premium Access
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          Join thousands making informed subscription decisions
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 2 }}>
        {/* Compact Billing Toggle - Centered */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
            overflow: 'visible', // Ensure badges aren't cut off
          }}
        >
          <ToggleButtonGroup
            value={billingInterval}
            exclusive
            onChange={(e, newInterval) => {
              if (newInterval !== null) {
                setBillingInterval(newInterval);
              }
            }}
            size="small"
            aria-label="billing interval"
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'visible', // Ensure badges aren't cut off
              position: 'relative',
              '& .MuiToggleButton-root': {
                px: 2.5,
                py: 0.75,
                border: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: theme.palette.text.secondary,
                transition: 'all 0.2s ease',
                overflow: 'visible', // Ensure badges aren't cut off
                '&.Mui-selected': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  color: 'white',
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          >
            <ToggleButton value="month" aria-label="monthly billing">
              Monthly
            </ToggleButton>
            {yearlyAvailable && (
            <ToggleButton 
              value="year" 
              aria-label="yearly billing"
                sx={{ 
                  position: 'relative',
                  overflow: 'visible',
                  pr: savingsPercent > 0 ? 1.5 : 2.5, // Add extra padding on right if badge exists
                }}
            >
              Yearly
              {savingsPercent > 0 && (
                <Chip
                  label={`-${savingsPercent}%`}
                  size="small"
                  sx={{
                    position: 'absolute',
                      top: -10,
                      right: -10,
                      height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                      zIndex: 1,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '& .MuiChip-label': {
                      px: 0.75,
                    },
                  }}
                />
              )}
            </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        {/* Perks Section */}
        <Box sx={{ mb: 4 }}>
          
          <Grid container spacing={3} sx={{ width: '100%' }}>
            {perks.map((perk, index) => (
              <Grid item xs={12} key={index} sx={{ width: '100%' }}>
                <Card
                  sx={{
                    height: 120,
                    width: '100%',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 48,
                          height: 48,
                          flexShrink: 0,
                        }}
                      >
                        {perk.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          {perk.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {perk.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* Social Proof */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            "OnlyStats VIP saved me hundreds in wasted subscriptions. Finally, a way to know which creators are worth it."
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            - Mike R., VIP Member
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 4, 
        pb: 4, 
        pt: 2,
        flexDirection: 'column',
        gap: 2
      }}>
        <Box sx={{ width: '100%' }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700} color="text.secondary">
              Have a promo code?
            </Typography>
          </Divider>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              fullWidth
              label="Promo code"
              value={promoCode}
              onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleRedeemPromo();
              }}
              inputProps={{ maxLength: 32 }}
            />
            <Button
              variant="outlined"
              onClick={handleRedeemPromo}
              disabled={promoLoading || !promoCode.trim()}
              sx={{ minWidth: { sm: 190 }, minHeight: 56, textTransform: 'none', fontWeight: 700 }}
            >
              {promoLoading ? <CircularProgress size={20} /> : 'Start free access'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Redeem eligible access without entering a credit card.
          </Typography>
          {promoError && <Alert severity="error" sx={{ mt: 1.5 }}>{promoError}</Alert>}
        </Box>

        <Button
          variant="contained"
          size="large"
          onClick={handleSubscribe}
          disabled={loading || loadingPlans || !selectedPlanData}
          fullWidth
          sx={{
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : loadingPlans ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading plans...
            </>
          ) : selectedPlanData ? (
            `Subscribe to VIP - ${(selectedPlanData.unitAmount / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: selectedPlanData.currency.toUpperCase()
            })}/${selectedPlanData.interval}`
          ) : (
            'Select a plan'
          )}
        </Button>
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Cancel anytime • Secure payment via Stripe • 30-day money-back guarantee
        </Typography>
      </DialogActions>
    </Dialog>
  );
}
